import * as styles from "./HeaderTablet.module.scss";

import { navigate } from "@reach/router";
import cx from "classnames";
import React, { useEffect, useState } from "react";
import { useCallback } from "react";
import MyAccountIcon from "../../../../components/MyAccountIcon/MyAccountIcon";
import MyBasketIcon from "../../../../components/MyBasketIcon/MyBasketIcon";
import MyFavouritesIcon from "../../../../components/MyFavouritesIcon/MyFavouritesIcon";
import ToolBar from "../../../../components/ToolBar/ToolBar";
import { Button, TextInput } from "../../../../components/common";
import {
	Chevron,
	PrimaryLogo,
	PrimaryLogoReduced,
	Search,
} from "../../../../components/common/Icons";
import { useMyAccountUK } from "../../../../context";
import { Colours } from "../../../../utils/colours";
import { isSpecificSite } from "../../../../utils/isSpecificSite";
import { getLogoSize } from "../Header.helpers";
import SkipToContent from "../SkipToContent/SkipToContent";
import { GOOGLE_SEARCH_BOX_ID, GOOGLE_SEARCH_BUTTON_CLASS } from "../consts";
import BurgerMenuTablet from "./BurgerMenuTablet";

const SCROLL_BREAK_POINT = 600;
export interface HeaderProps {
	nestedLinks: DogsTrust.MenuItem[];
	headerHeight: number;
	scrollHeight: number;
	breadcrumbs: DogsTrust.Breadcrumb[];
	promoCards: DogsTrust.PromotionCardToDisplayProps[];
}

export const HeaderTablet: React.FC<HeaderProps> = ({
	nestedLinks,
	headerHeight,
	scrollHeight,
	breadcrumbs,
	promoCards,
}) => {
	isSpecificSite("uk", "HeaderTablet");
	const { profile } = useMyAccountUK();
	const [showBurgerMenu, setShowBurgerMenu] = useState(false);
	const [iconSize, setIconSize] = useState(120);
	const [searchTerm, setSearchTerm] = useState("");
	const [prevScrollHeight, setPrevScrollHeight] = useState(scrollHeight);

	const handleSearch = useCallback(() => {
		const searchBox = document.getElementById(
			GOOGLE_SEARCH_BOX_ID,
		) as HTMLInputElement;
		const searchButton = document.getElementsByClassName(
			GOOGLE_SEARCH_BUTTON_CLASS,
		)[0] as HTMLButtonElement;

		if (searchBox && searchButton) {
			searchBox.value = searchTerm;
			searchBox.dispatchEvent(new KeyboardEvent("keydown", { key: "Enter" }));
			searchButton.click();
		} else navigate(`/search?q=${searchTerm}`);
	}, [searchTerm]);

	const submitHandler: React.FormEventHandler<HTMLFormElement> = (e) => {
		e.preventDefault();
		setShowBurgerMenu(false);
		handleSearch();
	};

	useEffect(() => {
		setShowBurgerMenu(false);
		setIconSize(getLogoSize(iconSize, prevScrollHeight, scrollHeight));
		setPrevScrollHeight(scrollHeight);
	}, [iconSize, prevScrollHeight, scrollHeight]);

	const getContainerTop = () => {
		if (scrollHeight > SCROLL_BREAK_POINT) {
			return -106;
		}
	};
	useEffect(() => {
		if (showBurgerMenu) {
			// Disable scroll on body
			document.body.style.touchAction = "none";
			document.body.style.overflow = "hidden";
		} else {
			// Enable scroll on body
			document.body.style.touchAction = "initial";
			document.body.style.overflow = "unset";
		}
	}, [showBurgerMenu]);
	const logoSize = iconSize > 105 ? "standard" : "small";
	return (
		<>
			<SkipToContent />
			<header
				id="dt-header"
				className={styles.headerouter}
				style={{ top: getContainerTop() }}
			>
				<div className={styles.headerinner}>
					<div
						className={styles.content}
						style={{
							justifyContent:
								scrollHeight < SCROLL_BREAK_POINT
									? "space-between"
									: "flex-end",
						}}
					>
						<a
							href={
								process.env.GATSBY_IS_PREVIEW === "true"
									? `${process.env.GATSBY_PUBLISHED_BASE_URL}`
									: "/"
							}
							aria-label="Home"
							id={"logo-wrapper"}
							className={styles.logowrappertop}
							style={{
								display: scrollHeight < SCROLL_BREAK_POINT ? "flex" : "none",
							}}
						>
							{logoSize === "standard" ? (
								<span>
									<PrimaryLogo
										height={`${iconSize}px`}
										width={`${iconSize}px`}
									/>
								</span>
							) : (
								<span>
									<PrimaryLogoReduced
										height={`${iconSize}px`}
										width={`${iconSize}px`}
									/>
								</span>
							)}
						</a>
						<div className={styles.contentrightwrapper}>
							<div className={styles.controlswrapper}>
								<div
									id="sitelookup"
									role="search"
									className={styles.searchboxwrapper}
								>
									<form onSubmit={submitHandler}>
										<TextInput
											name="search"
											placeholder={"Search"}
											className={styles.searchbox}
											IconRight={Search}
											onIconRightCallback={handleSearch}
											onChange={(e) => setSearchTerm(e.currentTarget.value)}
											value={searchTerm}
										/>
									</form>
								</div>
								{process.env.GATSBY_FEATURE_FLAG_MY_ACCOUNT === "true" && (
									<div className={styles.accountwrapper}>
										<MyAccountIcon />
									</div>
								)}
								{profile.basket?.length > 0 && (
									<MyBasketIcon
										className={styles.basket}
										count={profile.basket?.length}
									/>
								)}
								{profile.favourites.length > 0 && (
									<MyFavouritesIcon
										className={styles.favs}
										count={profile.favourites.length}
									/>
								)}
								<div className={styles.donatewrapper}>
									<Button
										variant="donate"
										fullWidth={true}
										url={"/support-us/donation"}
										ariaLabel=""
										size={"small"}
									>
										Donate
									</Button>
								</div>
								<button
									type="button"
									className={styles.burgerwrapper}
									onClick={() => setShowBurgerMenu((show) => !show)}
									aria-label={"main menu"}
									aria-expanded={showBurgerMenu}
									onKeyDown={(e) => {
										if (e.key === "Enter") {
											e.preventDefault();
											setShowBurgerMenu((show) => !show);
										}
										if (e.key === "Tab" && !e.shiftKey && showBurgerMenu) {
											e.preventDefault();
											document.getElementById("primary-link-0")?.focus();
										}
									}}
									id={"burger-menu"}
								>
									<div
										className={cx(
											styles.burgerContainer,
											showBurgerMenu ? styles.opened : styles.closed,
										)}
									>
										<div className={cx(styles.bar, styles.bar1)} />
										<div className={cx(styles.bar, styles.bar2)} />
										<div className={cx(styles.bar, styles.bar3)} />
									</div>
								</button>
							</div>
						</div>
					</div>
				</div>
			</header>
			<ToolBar
				headerHeight={headerHeight}
				scrollHeight={scrollHeight}
				breadcrumbs={breadcrumbs}
				isHidden={showBurgerMenu}
			/>
			<div
				className={styles.backtotop}
				style={{
					display: scrollHeight < SCROLL_BREAK_POINT ? "none" : "flex",
				}}
				onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
				onKeyDown={(e) =>
					e.key === "Enter" && window.scrollTo({ top: 0, behavior: "smooth" })
				}
			>
				<Chevron height={"25px"} width={"25px"} fill={Colours.DAWN} />
			</div>

			<BurgerMenuTablet
				show={showBurgerMenu}
				nestedLinks={nestedLinks}
				promoCards={promoCards}
			/>
		</>
	);
};

export default HeaderTablet;
